/**
 * Automation Canvas Block Component (Edgeless View)
 *
 * Renders the automation block on the canvas/edgeless surface.
 * Follows the Note-Reduce Pattern for correct zoom/scale positioning.
 *
 * The block is the overview of one automation and reads like the sidebar's
 * start page, which edits it:
 * - Header: the title, settings, and the status in words ("2 of 4 ready",
 *   "Active", "Running", "Last run failed").
 * - Trigger card: what starts the automation, the next run and its switch.
 * - Input → Workflow → Output: one card each with its key value, joined by
 *   the flow arrow. A card names what is still open in it.
 * - Run row: the last runs as bars and the latest run in words.
 * Every part is a jump: it opens the sidebar at its setting.
 *
 * Readiness is the shared model (`computeAutomationReadiness`), evaluated
 * over the block's own config plus the facts the sidebar loaded.
 *
 * Local-first contract: everything above renders from persisted Y.js props
 * first. Runtime data (run history, live step) is an additive overlay from
 * `window.__automationRuntimeBridge`; when the bridge is absent or offline
 * the persisted state simply stays — no spinners, no layout flicker.
 */

import { I18n } from '@affine/i18n';
import { ColorScheme } from '@blocksuite/affine-model';
import {
  EDGELESS_BLOCK_CHILD_PADDING,
  EDGELESS_CANVAS_CARD_SHADOW,
} from '@blocksuite/affine-shared/consts';
import {
  SidebarExtensionIdentifier,
  ThemeProvider,
} from '@blocksuite/affine-shared/services';
import { Bound, type SerializedXYWH } from '@blocksuite/global/gfx';
import {
  ArrowRightSmallIcon,
  DatabaseTableViewIcon,
  DateTimeIcon,
  EditIcon,
  LinkedPageIcon,
  PlayIcon,
  PlusIcon,
  RedoIcon,
  SettingsIcon,
  SignalIcon,
  TableIcon,
} from '@blocksuite/icons/lit';
import {
  getGfxBlockElementModelSize,
  toGfxBlockComponent,
} from '@blocksuite/std';
import { GfxViewInteractionExtension } from '@blocksuite/std/gfx';
import {
  type AutomationReadiness,
  computeAutomationReadiness,
  countReadyParts,
  isActivatableTrigger,
  isRowTrigger,
  READINESS_PART_COUNT,
  type ReadinessFacts,
  type ReadinessInput,
  type ReadinessIssue,
  type ReadinessState,
  type ReadinessStep,
  type ReadinessTileId,
  statusTrackingGap,
  type TriggerType,
} from '@streamlain/automation-safety-contract';
import { darkThemeV2, lightThemeV2, themeToVar } from '@streamlain/theme/v2';
import { html, nothing, type TemplateResult } from 'lit';
import { state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { styleMap } from 'lit/directives/style-map.js';

import { AutomationBlockSchema } from '../schemas/index.js';
import { AutomationBlockComponent } from './automation-block.js';
import * as styles from './automation-canvas-block.css.js';
import { resolveAutomationCanvasSelection } from './automation-canvas-selection.js';
import {
  hasCustomAutomationTitle,
  readAutomationTitleEditorValue,
  resolveAutomationName,
  resolveAutomationTitleWrite,
} from './automation-name.js';
import { DEFAULT_CLEANUP_SIZE } from './cleanup-binding.js';
import { cleanupIcon } from './cleanup-icon.js';
import {
  formatCleanupTimestamp,
  getCleanupSummary,
} from './cleanup-summary.js';

// Database bridge for runtime name and column lookup
interface ColumnInfo {
  id: string;
  name: string;
  type: string;
}
interface DatabaseBridge {
  findDatabaseByBlockId?: (
    blockId: string
  ) => { databaseTitle: string; docId: string } | undefined;
  getColumnsForDatabase?: (blockId: string) => ColumnInfo[] | null;
}

// Runtime bridge for execution state (run history + live run).
// Structural mirror of AutomationRuntimeSnapshot in
// packages/frontend/core/src/modules/streamlain/services/automation-runtime-bridge.ts —
// blocksuite packages cannot import frontend/core, so the shape is
// re-declared here (same pattern as DatabaseBridge above).
interface AutomationRunSummaryLike {
  id: string;
  status: 'success' | 'error' | 'cancelled' | 'running';
  startedAt: number;
  completedAt?: number;
  durationMs?: number;
  error?: string;
  terminalReason?: string;
  stepCount?: number;
  cleanup?: {
    deleted: number;
    skippedChanged: number;
    skippedMissing: number;
    status: 'completed' | 'partial';
  };
  inputRows?: number;
  rowsWritten?: number;
}
interface AutomationActiveRunLike {
  startedAt: number;
  stepName?: string;
  stepIndex?: number;
  stepCount?: number;
  percent?: number;
}
interface AutomationRuntimeSnapshotLike {
  history: AutomationRunSummaryLike[];
  activeRun: AutomationActiveRunLike | null;
  schedule: {
    enabled: boolean;
    pausedReason: string | null;
    lastRunAt: number | null;
    nextRunAt: number | null;
  } | null;
  readinessFacts?: (ReadinessFacts & { workflowId: string | null }) | null;
  access?: AutomationAccessLike | null;
  heldRows?: number;
}
/** Who the automation runs as and, per step, whether the reader may use it. */
interface AutomationAccessLike {
  runsAs: {
    name: string | null;
    avatarUrl: string | null;
    isReader: boolean;
  } | null;
  steps: Record<string, 'usable' | 'missing'>;
}
interface AutomationRuntimeBridge {
  getRuntime: (blockId: string) => AutomationRuntimeSnapshotLike | null;
  ensureRuntime: (blockId: string, docId?: string) => void;
  refreshSchedule?: (blockId: string, docId: string) => void;
  releaseRuntime?: (blockId: string) => void;
  resolveHeldRows?: (
    blockId: string,
    docId: string,
    action: 'run' | 'discard'
  ) => Promise<void>;
}

declare const window: Window & {
  __databaseBridge?: DatabaseBridge;
  __automationRuntimeBridge?: AutomationRuntimeBridge;
};

export const AFFINE_AUTOMATION_CANVAS = 'affine-automation-canvas';

const AUTOMATION_V2_THEME_KEYS = [
  'button/primary',
  'button/pureWhiteText',
  'button/success',
  'icon/disable',
  'icon/primary',
  'icon/secondary',
  'icon/tertiary',
  'layer/background/error',
  'layer/background/hoverOverlay',
  'layer/background/overlayPanel',
  'layer/background/primary',
  'layer/background/secondary',
  'layer/blackAlpha/10',
  'layer/insideBorder/blackBorder',
  'layer/insideBorder/border',
  'status/error',
  'status/success',
  'text/highlight/fg/orange',
  'text/primary',
  'text/secondary',
  'text/tertiary',
  'toast/iconState/regular',
] as const;

export function resolveAutomationCanvasThemeVariables(theme: ColorScheme) {
  const table = theme === ColorScheme.Dark ? darkThemeV2 : lightThemeV2;
  return Object.fromEntries(
    AUTOMATION_V2_THEME_KEYS.map(key => [themeToVar(key), table[key]])
  );
}

function formatCountdown(ms: number): string {
  if (ms <= 0) return '0s';
  const totalSeconds = Math.ceil(ms / 1000);
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const totalMinutes = Math.floor(totalSeconds / 60);
  if (totalMinutes < 60) {
    return `${totalMinutes}:${String(totalSeconds % 60).padStart(2, '0')}`;
  }
  const totalHours = Math.floor(totalMinutes / 60);
  if (totalHours < 48) return `${totalHours}h ${totalMinutes % 60}m`;
  return `${Math.floor(totalHours / 24)}d`;
}

function formatDurationMs(ms: number): string {
  if (ms < 10_000) return `${(ms / 1000).toFixed(1)}s`;
  if (ms < 60_000) return `${Math.round(ms / 1000)}s`;
  const minutes = Math.floor(ms / 60_000);
  const seconds = Math.round((ms % 60_000) / 1000);
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

/** Bars rendered in the health footer (newest run is the rightmost bar). */
const RUN_BAR_COUNT = 12;
const RUN_BAR_MIN_HEIGHT = 5;
const RUN_BAR_MAX_HEIGHT = 16;
/** Workflow steps listed in the block; the rest is counted. */
const MAX_LISTED_WORKFLOW_STEPS = 4;
const WORKFLOW_FRAME_STEP_TYPES = new Set(['start', 'end']);

const TRIGGER_ICONS: Record<TriggerType, () => TemplateResult> = {
  manual: () => PlayIcon(),
  scheduled: () => DateTimeIcon(),
  row_created: () => PlusIcon(),
  row_changed: () => EditIcon(),
  webhook: () => SignalIcon(),
};

const STEP_ICONS: Record<ReadinessStep, () => TemplateResult> = {
  input: () => TableIcon(),
  workflow: () => PlayIcon(),
  output: () => TableIcon(),
};

/** The setting a card opens at when nothing in it is open. */
const STEP_PRIMARY_TILE: Record<ReadinessStep, ReadinessTileId> = {
  input: 'source',
  workflow: 'workflow',
  output: 'target',
};

const RUN_STATUS_LABEL_KEYS: Record<
  AutomationRunSummaryLike['status'],
  string
> = {
  success: 'com.streamlain.streamlain.history.status-completed',
  error: 'com.streamlain.streamlain.history.status-failed',
  cancelled: 'com.streamlain.streamlain.history.status-cancelled',
  running: 'com.streamlain.streamlain.execution.status.running',
};

const READINESS_RUN_STATUS: Record<
  AutomationRunSummaryLike['status'],
  NonNullable<ReadinessInput['lastRun']>['status']
> = {
  success: 'completed',
  error: 'failed',
  cancelled: 'cancelled',
  running: 'running',
};

type BlockStatus = 'setup' | 'ready' | 'active' | 'running' | 'failed';

interface BlockCard {
  role: string;
  /** The setting the card opens. */
  tile: ReadinessTileId;
  icon: TemplateResult;
  label: string;
  /** The key value; without one the card shows what is open instead. */
  value: string | null;
  crossDoc?: boolean;
  meta?: string;
  issue?: ReadinessIssue;
  state: ReadinessState;
  /** Replaces the jump chevron (the trigger's switch). */
  control?: TemplateResult;
  body?: TemplateResult | typeof nothing;
}

export class AutomationCanvasBlockComponent extends toGfxBlockComponent(
  AutomationBlockComponent
) {
  private _autoMeasuredTransientXYWH: SerializedXYWH | null = null;

  override connectedCallback(): void {
    super.connectedCallback();
    this.style.borderRadius = '8px';
    this.style.boxShadow = EDGELESS_CANVAS_CARD_SHADOW;

    const selection = this.gfx.selection;

    this._selected = selection.has(this.model.id);
    this._editing = this._selected && selection.editing;
    this._disposables.add(
      selection.slots.updated.subscribe(() => {
        this._selected = selection.has(this.model.id);
        this._editing = this._selected && selection.editing;
      })
    );

    const titleText = this.model.props.title;
    const titleUpdate = () => this.requestUpdate();
    if (titleText?.yText) {
      titleText.yText.observe(titleUpdate);
      this._disposables.add(() => {
        titleText.yText.unobserve(titleUpdate);
      });
    }

    // Re-render when any prop changes (e.g. workflowName set by sidebar)
    this._disposables.add(
      this.model.propsUpdated.subscribe(() => {
        this._rebaseAutoMeasuredTransientXYWH();
        this.requestUpdate();
      })
    );

    // Re-render when database discovery bridge becomes available or updates
    // (bridge is set asynchronously by React — without this, columns show as empty on page load)
    const onBridgeUpdate = () => this.requestUpdate();
    window.addEventListener('database-discovery-updated', onBridgeUpdate);
    this._disposables.add(() => {
      window.removeEventListener('database-discovery-updated', onBridgeUpdate);
    });

    // Attach runtime bridge (run history + live run) and re-render on updates.
    // ensureRuntime lazily fetches persisted history once the backend is
    // reachable; offline it is deferred without any visible loading state.
    window.__automationRuntimeBridge?.ensureRuntime(
      this.model.id,
      this.model.store.id
    );
    const onRuntimeReady = () => {
      window.__automationRuntimeBridge?.ensureRuntime(
        this.model.id,
        this.model.store.id
      );
      this.requestUpdate();
    };
    window.addEventListener('automation-runtime-ready', onRuntimeReady);
    const onRuntimeUpdate = (event: Event) => {
      const detail = (event as CustomEvent<{ blockId: string }>).detail;
      if (detail?.blockId === this.model.id) {
        this.requestUpdate();
      }
    };
    window.addEventListener('automation-runtime-updated', onRuntimeUpdate);
    this._disposables.add(() => {
      window.removeEventListener('automation-runtime-ready', onRuntimeReady);
      window.removeEventListener('automation-runtime-updated', onRuntimeUpdate);
      window.__automationRuntimeBridge?.releaseRuntime?.(this.model.id);
    });

    // 1Hz ticker for the next-run countdown and the live "running for"
    // elapsed display. Only re-renders while something time-based is visible.
    const tickerId = setInterval(() => {
      if (this._needsTicker()) {
        this.requestUpdate();
      }
      const schedule = window.__automationRuntimeBridge?.getRuntime(
        this.model.id
      )?.schedule;
      if (
        schedule?.enabled &&
        schedule.nextRunAt !== null &&
        schedule.nextRunAt <= Date.now()
      ) {
        window.__automationRuntimeBridge?.refreshSchedule?.(
          this.model.id,
          this.model.store.id
        );
      }
    }, 1000);
    this._disposables.add(() => clearInterval(tickerId));

    // Re-render when sidebar writes a new title (may be hidden behind workflow viewer overlay)
    const onTitleChanged = () => this.requestUpdate();
    window.addEventListener('automation-title-changed', onTitleChanged);
    this._disposables.add(() => {
      window.removeEventListener('automation-title-changed', onTitleChanged);
    });

    // Workflow deletion handling is done at editor level (blocksuite-editor.tsx)
    // where block.model.props gives Y.js Text objects (not string proxies).
    // The Lit reactive proxy returns strings, making Y.js Text mutation impossible here.
  }

  private _emitAutoMeasuredTransientGeometryUpdated() {
    this.gfx.grid.update(this.model);
    this.gfx.selection.slots.transientGeometryUpdated.next(this.model.id);
  }

  private _setAutoMeasuredTransientXYWH(nextXYWH: SerializedXYWH | null) {
    const previousXYWH = this._autoMeasuredTransientXYWH;
    this._autoMeasuredTransientXYWH = nextXYWH;

    if (!nextXYWH) {
      if (previousXYWH && this.model.transientXYWH === previousXYWH) {
        this.model.transientXYWH = undefined;
        this.refreshGfxTransform();
        this._emitAutoMeasuredTransientGeometryUpdated();
        this.requestUpdate();
      }
      return;
    }

    const currentXYWH = this.model.transientXYWH;
    if (currentXYWH === nextXYWH) return;
    if (currentXYWH && currentXYWH !== previousXYWH) return;

    this.model.transientXYWH = nextXYWH;
    // Apply the same transient bound to the block host before notifying
    // selection consumers. A Lit update alone is asynchronous, so the
    // selection overlay could otherwise move one frame ahead of the block.
    // This is presentation-only: transientXYWH never enters the Yjs history.
    this.refreshGfxTransform();
    this._emitAutoMeasuredTransientGeometryUpdated();
    this.requestUpdate();
  }

  private _rebaseAutoMeasuredTransientXYWH() {
    if (
      !this._autoMeasuredTransientXYWH ||
      this.model.transientXYWH !== this._autoMeasuredTransientXYWH
    ) {
      return;
    }

    const baseBound = Bound.deserialize(this.model.props.xywh);
    const transientBound = Bound.deserialize(this._autoMeasuredTransientXYWH);
    transientBound.x = baseBound.x;
    transientBound.y = baseBound.y;
    transientBound.w = baseBound.w;
    this._setAutoMeasuredTransientXYWH(transientBound.serialize());
  }

  override firstUpdated() {
    const container = this.renderRoot.querySelector(
      '.automation-canvas-content'
    ) as HTMLElement | null;
    if (!container) return;

    const updateHeight = () => {
      // Title-editor updates are internal state. Recomputing geometry while
      // the input is active can recreate the GFX view and steal focus.
      if (this._isEditingTitle) return;
      const { height } = getGfxBlockElementModelSize(container);
      if (!height) return;

      const contentHeight =
        Math.max(height, container.scrollHeight) +
        2 * EDGELESS_BLOCK_CHILD_PADDING;

      const bound = Bound.deserialize(this.model.props.xywh);
      const cleanup = this.model.props.executionKind === 'cleanup';
      // The automation block is as tall as its content; the cleanup card
      // keeps the size it was given.
      const targetHeight = cleanup
        ? Math.max(contentHeight, bound.h, DEFAULT_CLEANUP_SIZE)
        : contentHeight;
      const heightChanged = Math.abs(bound.h - targetHeight) > 1;
      if (!heightChanged) {
        this._setAutoMeasuredTransientXYWH(null);
        return;
      }

      bound.h = targetHeight;
      // Measurement is presentation state. Never write Y.Doc geometry merely
      // because a block loaded, a runtime chip changed, or CSS reflowed.
      this._setAutoMeasuredTransientXYWH(bound.serialize());
    };

    requestAnimationFrame(updateHeight);

    const observer = new MutationObserver(() => {
      requestAnimationFrame(updateHeight);
    });
    observer.observe(container, { childList: true, subtree: true });
    this._disposables.add(() => {
      observer.disconnect();
      this._setAutoMeasuredTransientXYWH(null);
    });
  }

  override getRenderingRect() {
    const bound = Bound.deserialize(
      this.model.transientXYWH ?? this.model.props.xywh
    );

    return {
      x: bound.x,
      y: bound.y,
      w: bound.w,
      h: bound.h,
      zIndex: this.toZIndex(),
    };
  }

  override renderGfxBlock() {
    const edgelessTheme = this.std.get(ThemeProvider).theme$.value;
    const style = {
      ...resolveAutomationCanvasThemeVariables(edgelessTheme),
      borderRadius: '8px',
    };

    return html`
      <div
        class=${styles.automationContainer}
        style=${styleMap(style)}
        data-editing=${this._editing}
      >
        <automation-canvas-background
          .editing=${this._editing}
          .automation=${this.model}
        ></automation-canvas-background>
        <div
          class=${styles.clipContainer}
          style=${styleMap({ overflow: 'clip' })}
        >
          <div class="automation-canvas-content ${styles.canvasContent}">
            ${this.renderPageContent()}
          </div>
        </div>
        <automation-canvas-mask
          .model=${this.model}
          .host=${this.host}
          .zoom=${this.gfx.viewport.zoom ?? 1}
          .disableMask=${this._selected}
          .editing=${this._editing}
          @pointerdown=${this.activateCanvasSelectionFromMask}
        ></automation-canvas-mask>
      </div>
    `;
  }

  private readonly activateCanvasSelectionFromMask = (event: PointerEvent) => {
    if (event.button !== 0 || !event.isPrimary || this.model.isLocked()) {
      return;
    }
    event.stopPropagation();
    this.gfx.selection.set({
      elements: resolveAutomationCanvasSelection({
        additive: event.shiftKey || event.ctrlKey || event.metaKey,
        selectedIds: this.gfx.selection.selectedIds,
        targetId: this.model.id,
      }),
      editing: false,
    });
  };

  override renderPageContent() {
    const { model } = this;
    if (model.props.executionKind === 'cleanup') return this._renderCleanup();

    const runtime =
      window.__automationRuntimeBridge?.getRuntime(model.id) ?? null;
    const activeRun = runtime?.activeRun ?? null;
    const history = runtime?.history ?? [];
    const readiness = this._readiness(runtime);

    return html`
      <div class=${styles.content}>
        ${this.renderDeletionPendingNotice()}
        ${this._renderHeader(
          !!model.props.workflowId,
          html`${this._renderStatus(readiness, activeRun, history, runtime)}
          ${this._renderHeldRows(runtime)}`
        )}
        ${this._renderTriggerCard(readiness)}
        <ol class=${styles.flow} data-role="automation-flow">
          <li class=${styles.flowItem}>
            ${this._renderInputCard(readiness)} ${this._renderConnector()}
          </li>
          <li class=${styles.flowItem}>
            ${this._renderWorkflowCard(readiness)} ${this._renderConnector()}
          </li>
          <li class=${styles.flowItem}>${this._renderOutputCard(readiness)}</li>
        </ol>
        ${this._renderRunRow(activeRun, history)}
      </div>
    `;
  }

  /**
   * The shared readiness model over the block's own config. The facts only
   * the sidebar loads (workflow, schema, source kind, AI keys, safety) count
   * once it has shown this automation with the same workflow; until then
   * they are unknown, as the model allows.
   */
  private _readiness(
    runtime: AutomationRuntimeSnapshotLike | null
  ): AutomationReadiness {
    const props = this.model.props;
    const shared = runtime?.readinessFacts;
    const facts =
      shared && shared.workflowId === (props.workflowId ?? null)
        ? shared
        : null;
    const latest = runtime?.activeRun
      ? ({ status: 'running' } as const)
      : runtime?.history[0];
    const statusColumnCandidate =
      !!props.sourceTableId &&
      (
        window.__databaseBridge?.getColumnsForDatabase?.(props.sourceTableId) ??
        []
      ).some(column => column.type === 'select');

    return computeAutomationReadiness({
      config: props,
      workflow: facts?.workflow ?? null,
      schema: facts?.schema ?? null,
      sourceIsRowless: facts?.sourceIsRowless ?? false,
      hasStatusColumnCandidate:
        facts?.hasStatusColumnCandidate ?? statusColumnCandidate,
      statusColumnShared: facts?.statusColumnShared ?? false,
      missingAiKeys: facts?.missingAiKeys ?? [],
      hasTestContext: facts?.hasTestContext ?? false,
      safety: facts?.safety ?? null,
      lastRun: latest
        ? {
            status: READINESS_RUN_STATUS[latest.status],
            terminalReason:
              'terminalReason' in latest ? latest.terminalReason : undefined,
          }
        : null,
    });
  }

  private _defaultTitle() {
    return this.model.props.executionKind === 'cleanup'
      ? I18n.t('com.streamlain.streamlain.cleanup.title')
      : I18n.t('com.streamlain.streamlain.common.new-automation');
  }

  private _renderCleanup() {
    const summary = getCleanupSummary(this.model.props);
    const runtime = window.__automationRuntimeBridge?.getRuntime(this.model.id);
    const nextRun = summary.enabled ? runtime?.schedule?.nextRunAt : null;
    const lastResult = runtime?.history[0]?.cleanup;
    return html`<div class=${styles.cleanupContent} data-role="cleanup-card">
      ${this._renderHeader(false, nothing)}
      <div class=${styles.cleanupRow} title=${summary.databaseName ?? ''}>
        ${DatabaseTableViewIcon({ width: '16px', height: '16px' })}
        ${summary.databaseName ||
        I18n.t('com.streamlain.streamlain.cleanup.database-empty')}
      </div>
      <div class=${styles.cleanupRow}>
        ${cleanupIcon(18)}
        ${summary.days !== null
          ? I18n.t('com.streamlain.streamlain.cleanup.rule-days', {
              days: summary.days,
            })
          : summary.ruleCount
            ? I18n.t('com.streamlain.streamlain.cleanup.rule-count', {
                count: summary.ruleCount,
              })
            : I18n.t('com.streamlain.streamlain.cleanup.rule-empty')}
      </div>
      <div class=${styles.cleanupRow} data-role="cleanup-state">
        ${summary.enabled
          ? summary.preset === 'nightly' || !summary.preset
            ? I18n.t('com.streamlain.streamlain.cleanup.next-nightly')
            : this._formatTrigger(this.model.props.triggerConfig)
          : I18n.t('com.streamlain.streamlain.cleanup.paused')}
      </div>
      <div class=${styles.cleanupSchedule}>
        ${I18n.t('com.streamlain.streamlain.cleanup.next-run')}:
        ${nextRun
          ? (formatCleanupTimestamp(nextRun, summary.timezone) ?? '—')
          : '—'}
        ${summary.timezone ?? ''}
      </div>
      <div class=${styles.cleanupSchedule}>
        ${I18n.t('com.streamlain.streamlain.cleanup.last-run')}:
        ${formatCleanupTimestamp(
          runtime?.history[0]?.completedAt ?? summary.lastRunAt,
          summary.timezone
        ) ?? '—'}
      </div>
      ${lastResult
        ? html`<div class=${styles.cleanupSchedule} data-role="cleanup-result">
            ${I18n.t('com.streamlain.streamlain.cleanup.result', {
              deleted: lastResult.deleted,
              changed: lastResult.skippedChanged,
              missing: lastResult.skippedMissing,
            })}
            ${lastResult.status === 'partial'
              ? I18n.t('com.streamlain.streamlain.cleanup.partial')
              : ''}
          </div>`
        : ''}
    </div>`;
  }

  @state()
  private accessor _selected = false;

  @state()
  private accessor _editing = false;

  @state()
  private accessor _isEditingTitle = false;

  @state()
  private accessor _titleDraft = '';

  private _needsTicker(): boolean {
    const triggerConfig = this.model.props.triggerConfig;
    const schedule = window.__automationRuntimeBridge?.getRuntime(
      this.model.id
    )?.schedule;
    if (
      triggerConfig?.type === 'scheduled' &&
      (schedule?.enabled ?? triggerConfig.enabled)
    ) {
      return true;
    }
    return !!window.__automationRuntimeBridge?.getRuntime(this.model.id)
      ?.activeRun;
  }

  // ===========================================================================
  // Header — title, settings, status
  // ===========================================================================

  private _renderHeader(
    hasWorkflow: boolean,
    status: TemplateResult | typeof nothing
  ) {
    const model = this.model;
    const automationIdentity = {
      title: model.props.title,
      titleMode: model.props.titleMode,
      workflowName: model.props.workflowName,
    };
    const displayTitle = resolveAutomationName(
      automationIdentity,
      this._defaultTitle()
    );
    const hasMeaningfulTitle =
      hasWorkflow || hasCustomAutomationTitle(automationIdentity);
    const openSettings = I18n.t(
      'com.streamlain.streamlain.process.open-settings'
    );

    return html`
      <div class=${styles.header} data-role="automation-header">
        <div class=${styles.headerTop}>
          <div class=${styles.headerIdentity}>
            ${this._isEditingTitle
              ? html`<input
                  class=${classMap({
                    [styles.titleInput]: true,
                    [styles.titleTextEmpty]: !hasMeaningfulTitle,
                  })}
                  data-automation-title-editor
                  .value=${this._titleDraft}
                  title=${displayTitle}
                  aria-label=${displayTitle}
                  @input=${this._handleTitleInput}
                  @keydown=${this._handleTitleKeyDown}
                  @blur=${this._handleTitleBlur}
                  @pointerdown=${this._stopTitleEditorPointer}
                />`
              : html`<div
                  class=${classMap({
                    [styles.titleText]: true,
                    [styles.titleTextEmpty]: !hasMeaningfulTitle,
                  })}
                  data-automation-title-editor
                  title=${displayTitle}
                  @pointerdown=${this._startTitleEditing}
                >
                  ${displayTitle}
                </div>`}
          </div>
          <button
            class=${styles.iconButton}
            data-role="automation-open-settings"
            aria-label=${openSettings}
            title=${openSettings}
            @click=${this._openSettings()}
            @pointerdown=${this._stopPointer}
          >
            ${SettingsIcon()}
          </button>
        </div>
        ${status}
      </div>
    `;
  }

  /**
   * The automation's state in words, then who it runs as. It opens what the
   * state is about: the first open setting, the history of a failed run, else
   * the start page.
   */
  private _renderStatus(
    readiness: AutomationReadiness,
    activeRun: AutomationActiveRunLike | null,
    history: AutomationRunSummaryLike[],
    runtime: AutomationRuntimeSnapshotLike | null
  ) {
    const failedRun = !activeRun && this._lastRunFailed(history);
    const status: BlockStatus = activeRun
      ? 'running'
      : readiness.status === 'setup'
        ? 'setup'
        : failedRun
          ? 'failed'
          : readiness.status;
    const firstGap = readiness.issues.find(
      issue => issue.severity === 'missing'
    );
    const label =
      status === 'running'
        ? I18n.t('com.streamlain.streamlain.execution.status.running')
        : status === 'setup'
          ? I18n.t('com.streamlain.automation.sidebar.status.steps-ready', {
              ready: countReadyParts(readiness),
              total: READINESS_PART_COUNT,
            })
          : status === 'failed'
            ? I18n.t('com.streamlain.automation.sidebar.last-run-failed')
            : I18n.t(`com.streamlain.automation.sidebar.status.${status}`);
    const open =
      status === 'setup' && firstGap
        ? this._openSettings(firstGap.tile, true)
        : status === 'failed' || status === 'running'
          ? this._openSettings('history')
          : this._openSettings();

    return html`
      <button
        class=${styles.statusPill}
        data-role="automation-status"
        data-status=${status}
        @click=${open}
        @pointerdown=${this._stopPointer}
      >
        <span class=${styles.statusDot} aria-hidden="true"></span>
        <span class=${styles.statusLabel}>${label}</span>
        ${this._renderStatusPerson(runtime)}
        <span class=${styles.statusChevron} aria-hidden="true"
          >${ArrowRightSmallIcon()}</span
        >
      </button>
    `;
  }

  private _lastRunFailed(history: AutomationRunSummaryLike[]): boolean {
    const latest = history.find(run => run.status !== 'running');
    return latest
      ? latest.status === 'error'
      : this.model.props.lastRunStatus === 'error';
  }

  private readonly _startTitleEditing = (event: PointerEvent) => {
    if (this.model.store.readonly) return;
    event.preventDefault();
    event.stopPropagation();
    if (this.model.isLocked()) return;

    this.gfx.selection.set({
      elements: [this.model.id],
      editing: true,
    });
    this._isEditingTitle = true;
    this._titleDraft = resolveAutomationName(
      {
        title: this.model.props.title,
        titleMode: this.model.props.titleMode,
        workflowName: this.model.props.workflowName,
      },
      this._defaultTitle()
    );
    this.updateComplete
      .then(() => {
        const input = this.renderRoot.querySelector<HTMLInputElement>(
          `.${styles.titleInput}`
        );
        input?.focus();
        input?.select();
      })
      .catch(console.error);
  };

  private readonly _stopTitleEditorPointer = (event: PointerEvent) => {
    event.stopPropagation();
  };

  private readonly _handleTitleInput = (event: Event) => {
    const input = event.target as HTMLInputElement;
    this._titleDraft = readAutomationTitleEditorValue(input, this._titleDraft);
  };

  private readonly _handleTitleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      this._commitTitle();
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      this._isEditingTitle = false;
      this._titleDraft = resolveAutomationName(
        {
          title: this.model.props.title,
          titleMode: this.model.props.titleMode,
          workflowName: this.model.props.workflowName,
        },
        this._defaultTitle()
      );
      return;
    }
    event.stopPropagation();
  };

  private readonly _handleTitleBlur = () => {
    this._commitTitle();
  };

  private readonly _commitTitle = () => {
    if (!this._isEditingTitle) return;

    const input = this.renderRoot.querySelector<HTMLInputElement>(
      `.${styles.titleInput}`
    );
    this._isEditingTitle = false;
    const titleDraft = readAutomationTitleEditorValue(
      input,
      this._titleDraft
    ).trim();

    const next = resolveAutomationTitleWrite(
      {
        title: this.model.props.title,
        titleMode: this.model.props.titleMode,
        workflowName: this.model.props.workflowName,
      },
      titleDraft,
      'user'
    );
    if (!next) return;

    this.model.store.captureSync();
    this.model.store.transact(() => {
      this.model.props.titleMode = next.titleMode;
      const title = this.model.props.title;
      title.replace(0, title.length, next.title);
    });
    window.dispatchEvent(new CustomEvent('automation-title-changed'));
  };

  // ===========================================================================
  // Cards — trigger, then Input → Workflow → Output
  // ===========================================================================

  /** One card; all of it opens its setting in the sidebar. */
  private _renderCard(card: BlockCard) {
    const shown = card.value ?? (card.issue ? I18n.t(card.issue.labelKey) : '');
    const below =
      card.value && card.issue ? I18n.t(card.issue.labelKey) : card.meta;

    return html`
      <section
        class=${styles.card}
        data-role=${card.role}
        data-state=${card.state}
      >
        <button
          class=${styles.cardToggle}
          aria-label="${card.label}: ${shown}"
          @click=${this._openSettings(card.tile)}
          @pointerdown=${this._stopPointer}
        ></button>
        <span class=${styles.cardIcon} aria-hidden="true">${card.icon}</span>
        <div class=${styles.cardText}>
          <span class=${styles.cardLabel}>${card.label}</span>
          <span
            class=${styles.cardValue}
            data-open=${!card.value && !!card.issue}
            title=${shown}
          >
            <span class=${styles.cardValueText}>${shown}</span>
            ${card.crossDoc
              ? html`<span class=${styles.cardValueIcon} aria-hidden="true"
                  >${LinkedPageIcon()}</span
                >`
              : nothing}
          </span>
          ${below
            ? html`<span
                class=${styles.cardMeta}
                data-open=${!!(card.value && card.issue)}
                title=${below}
                >${below}</span
              >`
            : nothing}
        </div>
        ${card.control ??
        html`<span class=${styles.cardChevron} aria-hidden="true"
          >${ArrowRightSmallIcon()}</span
        >`}
        ${card.body ?? nothing}
      </section>
    `;
  }

  /** A step's card: its state and first gap come from the readiness model. */
  private _renderStepCard(
    step: ReadinessStep,
    readiness: AutomationReadiness,
    card: Pick<BlockCard, 'value' | 'crossDoc' | 'meta' | 'body'>
  ) {
    const issue = readiness.steps[step].issues[0];
    return this._renderCard({
      ...card,
      role: step,
      tile: issue?.tile ?? STEP_PRIMARY_TILE[step],
      icon: STEP_ICONS[step](),
      label: I18n.t(`com.streamlain.automation.panel.group.${step}`),
      issue,
      state: readiness.steps[step].state,
    });
  }

  private _renderConnector() {
    return html`
      <div class=${styles.connector} data-role="automation-internal-edge">
        <svg
          class=${styles.connectorIcon}
          viewBox="0 0 20 38"
          aria-hidden="true"
          focusable="false"
        >
          <circle
            class=${styles.connectorHandle}
            data-marker="source-handle"
            cx="10"
            cy="0"
            r="6"
          ></circle>
          <path
            class=${styles.connectorShaft}
            data-marker="shaft"
            d="M10 0V27"
          ></path>
          <path
            class=${styles.connectorArrowhead}
            data-marker="target-arrow"
            d="M2 26L10 38L18 26Z"
          ></path>
        </svg>
      </div>
    `;
  }

  private _access(): AutomationAccessLike | null {
    return (
      window.__automationRuntimeBridge?.getRuntime(this.model.id)?.access ??
      null
    );
  }

  private _missingAccessCount(access: AutomationAccessLike | null): number {
    return access
      ? Object.values(access.steps).filter(state => state === 'missing').length
      : 0;
  }

  /**
   * Who the automation runs as, after the state in the status pill; its title
   * says whether it runs, is paused by the server or is off. Someone else's
   * automation is taken over in the sidebar.
   */
  private _renderStatusPerson(runtime: AutomationRuntimeSnapshotLike | null) {
    const runsAs = runtime?.access?.runsAs;
    if (!runsAs) return nothing;
    const name =
      runsAs.name ?? I18n.t('com.streamlain.automation.takeover.someone');
    const state = runtime?.schedule?.enabled
      ? 'on'
      : runtime?.schedule?.pausedReason
        ? 'paused'
        : 'off';
    return html`<span
      class=${styles.statusPerson}
      data-role="automation-runs-as"
      data-state=${state}
      title=${I18n.t(`com.streamlain.automation.block.runs-as.${state}`, {
        name,
      })}
    >
      ${runsAs.avatarUrl
        ? html`<img
            class=${styles.statusAvatar}
            src=${runsAs.avatarUrl}
            alt=""
          />`
        : html`<span class=${styles.statusAvatar} aria-hidden="true"
            >${name.slice(0, 1).toUpperCase()}</span
          >`}
      <span class=${styles.statusLabel}>${name}</span>
    </span>`;
  }

  /**
   * New rows the import brake held back: they start no run until someone
   * runs or discards them here.
   */
  private _renderHeldRows(runtime: AutomationRuntimeSnapshotLike | null) {
    const count = runtime?.heldRows ?? 0;
    if (count === 0) return nothing;
    const resolve = (action: 'run' | 'discard') => (event: Event) => {
      event.stopPropagation();
      void window.__automationRuntimeBridge?.resolveHeldRows?.(
        this.model.id,
        this.model.store.id,
        action
      );
    };
    return html`<div class=${styles.heldRows} data-role="automation-held-rows">
      <span class=${styles.noteText}
        >${I18n.t('com.streamlain.automation.block.held-rows', {
          count: String(count),
        })}</span
      >
      <button
        class=${styles.noteAction}
        data-role="automation-held-rows-run"
        @click=${resolve('run')}
        @pointerdown=${this._stopPointer}
      >
        ${I18n.t('com.streamlain.automation.block.held-rows.run')}
      </button>
      <button
        class=${styles.noteAction}
        data-role="automation-held-rows-discard"
        @click=${resolve('discard')}
        @pointerdown=${this._stopPointer}
      >
        ${I18n.t('com.streamlain.automation.block.held-rows.discard')}
      </button>
    </div>`;
  }

  /** Whether the reader may use a step's credentials, for its badge. */
  private _renderStepAccess(
    stepId: string,
    access: AutomationAccessLike | null
  ) {
    const state = access?.steps[stepId];
    if (!state) return nothing;
    const runsAs = access?.runsAs;
    const label =
      state === 'usable'
        ? I18n.t('com.streamlain.automation.block.step-access.usable')
        : runsAs && !runsAs.isReader
          ? I18n.t('com.streamlain.automation.block.step-access.from', {
              name:
                runsAs.name ??
                I18n.t('com.streamlain.automation.takeover.someone'),
            })
          : I18n.t('com.streamlain.automation.block.step-access.missing');
    return html`<span
      class=${styles.stepAccess}
      data-role="workflow-step-access"
      data-access=${state}
      >${label}</span
    >`;
  }

  private _triggerEnabled(): boolean {
    return (
      window.__automationRuntimeBridge?.getRuntime(this.model.id)?.schedule
        ?.enabled ??
      this.model.props.triggerConfig?.enabled ??
      false
    );
  }

  private _renderTriggerCard(readiness: AutomationReadiness) {
    const triggerConfig = this.model.props.triggerConfig;
    const type: TriggerType = triggerConfig?.type ?? 'manual';
    const activatable = isActivatableTrigger(type);
    const enabled = this._triggerEnabled();

    const runtime = window.__automationRuntimeBridge?.getRuntime(this.model.id);
    let meta: string | undefined;
    if (activatable && !enabled) {
      meta = I18n.t('com.streamlain.automation.block.trigger-off');
    } else if (type === 'scheduled') {
      const nextRun = runtime?.schedule?.nextRunAt ?? null;
      if (nextRun !== null) {
        meta = I18n.t('com.streamlain.automation.block.next-run', {
          time: formatCountdown(nextRun - Date.now()),
        });
      }
    }

    return this._renderCard({
      role: 'trigger',
      tile: 'trigger',
      icon: TRIGGER_ICONS[type](),
      label: I18n.t('com.streamlain.automation.panel.group.trigger'),
      value: this._formatTrigger(triggerConfig),
      meta,
      state: readiness.tiles.trigger,
      // A trigger that runs by itself is switched on and off right here; a
      // manual one is started right here once the automation can run.
      control: !this.model.props.workflowId
        ? undefined
        : activatable
          ? this._renderSwitch(enabled)
          : readiness.canRun
            ? this._renderRunButton(!!runtime?.activeRun)
            : undefined,
    });
  }

  private _renderRunButton(running: boolean) {
    const label = I18n.t('com.streamlain.streamlain.execution.btn.run');
    return html`
      <button
        class=${styles.runButton}
        aria-label=${label}
        title=${label}
        data-role="automation-run-now"
        ?disabled=${running}
        @click=${this._handleRun}
        @pointerdown=${this._stopPointer}
      >
        ${PlayIcon()}
      </button>
    `;
  }

  private _renderSwitch(enabled: boolean) {
    // Someone else's automation is changed only after taking it over; one
    // with credentials the reader may not use cannot be switched on.
    const access = this._access();
    const missing = this._missingAccessCount(access);
    const runsAs = access?.runsAs;
    const locked =
      enabled && runsAs && !runsAs.isReader
        ? I18n.t('com.streamlain.automation.block.switch-locked-runs-as', {
            name:
              runsAs.name ??
              I18n.t('com.streamlain.automation.takeover.someone'),
          })
        : !enabled && missing > 0
          ? I18n.t('com.streamlain.automation.block.switch-locked-missing', {
              count: missing,
            })
          : null;
    const label =
      locked ??
      (enabled
        ? I18n.t('com.streamlain.streamlain.process.click-to-disable')
        : I18n.t('com.streamlain.streamlain.process.click-to-enable'));
    return html`
      <button
        class=${styles.switchButton}
        role="switch"
        aria-checked=${enabled}
        aria-label=${label}
        title=${label}
        ?disabled=${locked !== null}
        data-role="automation-trigger-switch"
        @click=${this._handleToggleEnabled}
        @pointerdown=${this._stopPointer}
      >
        <span class=${styles.switchKnob}></span>
      </button>
    `;
  }

  private _renderInputCard(readiness: AutomationReadiness) {
    const props = this.model.props;
    const isWebhook = props.triggerConfig?.type === 'webhook';
    const source =
      props.sourceTableId && !isWebhook
        ? window.__databaseBridge?.findDatabaseByBlockId?.(props.sourceTableId)
        : undefined;
    const value = isWebhook
      ? I18n.t('com.streamlain.streamlain.process.canvas.webhook-input')
      : props.sourceTableId
        ? (source?.databaseTitle ??
          props.sourceTableName ??
          I18n.t('com.streamlain.automation.panel.tile.source'))
        : // Without a table the automation runs on what its trigger sends,
          // unless the readiness model asks for one.
          readiness.steps.input.issues.length > 0
          ? null
          : I18n.t('com.streamlain.automation.sidebar.source-none');

    const relatedCount = (props.inputTables ?? []).filter(
      table => table.role === 'secondary'
    ).length;
    const fieldCount =
      (props.inputMappings ?? []).filter(mapping => mapping.columnId).length ||
      (props.selectedColumns?.length ?? 0);

    return this._renderStepCard('input', readiness, {
      value,
      crossDoc: !!source && source.docId !== this.model.store.id,
      meta:
        props.sourceTableId || isWebhook
          ? this._facts([
              ['filters', this.model.filterCount],
              ['related', relatedCount],
              ['fields', fieldCount],
            ])
          : I18n.t('com.streamlain.automation.sidebar.source-none-hint'),
    });
  }

  private _renderWorkflowCard(readiness: AutomationReadiness) {
    const props = this.model.props;
    // Start and end frame every workflow; the card lists what it does.
    const steps = props.workflowId
      ? (props.workflowSteps ?? []).filter(
          step => !WORKFLOW_FRAME_STEP_TYPES.has(step.type)
        )
      : [];
    const access = this._access();
    const missing = this._missingAccessCount(access);
    const missingNotice =
      missing > 0
        ? html`<div
            class=${styles.accessMissing}
            role="status"
            data-role="automation-access-missing"
          >
            <span
              >${I18n.t('com.streamlain.automation.block.access-missing', {
                count: missing,
              })}</span
            >
            <button
              class=${styles.noteAction}
              @click=${this._openSettings()}
              @pointerdown=${this._stopPointer}
            >
              ${I18n.t('com.streamlain.automation.block.resolve')}
            </button>
          </div>`
        : nothing;

    return this._renderStepCard('workflow', readiness, {
      value: props.workflowId
        ? (props.workflowName ??
          I18n.t('com.streamlain.automation.panel.tile.workflow'))
        : null,
      body:
        steps.length > 0
          ? html`${missingNotice}
              <ol class=${styles.steps} data-role="workflow-step-list">
                ${steps.slice(0, MAX_LISTED_WORKFLOW_STEPS).map(
                  (step, index) =>
                    html`<li
                      class=${styles.step}
                      data-role="workflow-step"
                      title=${step.name}
                    >
                      <span class=${styles.stepIndex}>${index + 1}</span>
                      <span class=${styles.stepName}>${step.name}</span>
                      ${this._renderStepAccess(step.id, access)}
                    </li>`
                )}
                ${steps.length > MAX_LISTED_WORKFLOW_STEPS
                  ? html`<li class=${styles.stepMore}>
                      ${I18n.t('com.streamlain.automation.block.more-steps', {
                        count: steps.length - MAX_LISTED_WORKFLOW_STEPS,
                      })}
                    </li>`
                  : nothing}
              </ol>`
          : nothing,
    });
  }

  private _renderOutputCard(readiness: AutomationReadiness) {
    const props = this.model.props;
    const bridge = window.__databaseBridge;
    const tables = (props.outputTables ?? []).filter(table => table.tableId);
    const first = tables[0];
    const target = first
      ? bridge?.findDatabaseByBlockId?.(first.tableId)
      : undefined;
    const name = first
      ? (target?.databaseTitle ??
        first.tableName ??
        I18n.t('com.streamlain.automation.panel.tile.target'))
      : null;

    const matchKey = first?.matchConfigId
      ? (props.matchConfigs ?? []).find(
          config => config.id === first.matchConfigId
        )?.keys[0]
      : undefined;
    const matchColumn = matchKey
      ? bridge
          ?.getColumnsForDatabase?.(first.tableId)
          ?.find(column => column.id === matchKey.columnId)?.name
      : undefined;

    return this._renderStepCard('output', readiness, {
      value: name && tables.length > 1 ? `${name} +${tables.length - 1}` : name,
      crossDoc: !!target && target.docId !== this.model.store.id,
      meta: [
        matchColumn
          ? I18n.t('com.streamlain.automation.block.match', {
              name: matchColumn,
            })
          : '',
        this._facts([['fields', (props.outputMappings ?? []).length]]) ?? '',
      ]
        .filter(Boolean)
        .join(' · '),
    });
  }

  /** "Filters: 2 · Fields: 4": the counts above zero, in one line. */
  private _facts(
    counts: ReadonlyArray<readonly ['filters' | 'related' | 'fields', number]>
  ): string | undefined {
    const parts = counts
      .filter(([, count]) => count > 0)
      .map(([name, count]) =>
        I18n.t(`com.streamlain.automation.block.count.${name}`, { count })
      );
    return parts.length > 0 ? parts.join(' · ') : undefined;
  }

  // ===========================================================================
  // Run row — the last runs as bars, the latest run in words
  // ===========================================================================

  private _renderRunRow(
    activeRun: AutomationActiveRunLike | null,
    history: AutomationRunSummaryLike[]
  ) {
    const terminalRuns = history.filter(run => run.status !== 'running');
    const latest = terminalRuns[0];
    const lastRunAt = latest?.startedAt ?? this.model.props.lastRunAt;
    const lastStatus = latest?.status ?? this.model.props.lastRunStatus;
    const failed = !activeRun && this._lastRunFailed(history);
    const error = failed
      ? terminalRuns.find(run => run.status === 'error')?.error
      : undefined;

    let text: string;
    if (activeRun) {
      text = this._formatActiveRun(activeRun);
    } else if (lastRunAt) {
      text = [
        this._formatLastRun(lastRunAt),
        lastStatus && lastStatus !== 'running'
          ? I18n.t(RUN_STATUS_LABEL_KEYS[lastStatus])
          : '',
        latest?.rowsWritten
          ? I18n.t('com.streamlain.streamlain.process.canvas.rows-written', {
              count: latest.rowsWritten,
            })
          : '',
      ]
        .filter(Boolean)
        .join(' · ');
    } else {
      text = I18n.t('com.streamlain.automation.block.no-run');
    }
    const historyLabel = I18n.t('com.streamlain.streamlain.execution.history');
    // The live run's seconds tick in the text; the button's name stays the status.
    const runLabel = activeRun
      ? I18n.t('com.streamlain.streamlain.execution.status.running')
      : text;
    const retry = I18n.t('com.streamlain.streamlain.process.canvas.retry-run');

    return html`
      <div
        class=${styles.runRow}
        data-role="automation-run"
        data-state=${activeRun ? 'running' : failed ? 'failed' : 'idle'}
      >
        <div class=${styles.runLine}>
          <button
            class=${styles.runToggle}
            data-role="automation-open-history"
            aria-label="${historyLabel}: ${runLabel}"
            title=${lastRunAt
              ? new Date(lastRunAt).toLocaleString()
              : historyLabel}
            @click=${this._openSettings('history')}
            @pointerdown=${this._stopPointer}
          >
            ${this._renderRunBars(activeRun, terminalRuns)}
            <span class=${styles.runText}>${text}</span>
            <span class=${styles.cardChevron} aria-hidden="true"
              >${ArrowRightSmallIcon()}</span
            >
          </button>
          ${failed
            ? html`<button
                class=${styles.iconButton}
                data-role="automation-retry"
                aria-label=${retry}
                title=${retry}
                @click=${this._handleRun}
                @pointerdown=${this._stopPointer}
              >
                ${RedoIcon()}
              </button>`
            : nothing}
        </div>
        ${error
          ? html`<p class=${styles.runError} title=${error}>${error}</p>`
          : nothing}
      </div>
    `;
  }

  /** "Step 2 of 3 · running for 12s" in the one-line run row: a run never changes the block's height. */
  private _formatActiveRun(activeRun: AutomationActiveRunLike): string {
    const elapsed = I18n.t(
      'com.streamlain.streamlain.process.canvas.running-since',
      { time: formatCountdown(Date.now() - activeRun.startedAt) }
    );
    const progress =
      activeRun.stepIndex !== undefined && activeRun.stepCount
        ? I18n.t('com.streamlain.automation.block.step-progress', {
            index: activeRun.stepIndex,
            count: activeRun.stepCount,
          })
        : activeRun.stepName;
    return progress ? `${progress} · ${elapsed}` : elapsed;
  }

  private _renderRunBars(
    activeRun: AutomationActiveRunLike | null,
    terminalRuns: AutomationRunSummaryLike[]
  ) {
    if (terminalRuns.length === 0 && !activeRun) return nothing;

    // Oldest → newest, newest bar on the right; live run appended last.
    const runs = terminalRuns.slice(0, RUN_BAR_COUNT).reverse();

    const durations = runs
      .map(run => run.durationMs ?? 0)
      .filter(value => value > 0);
    const maxDuration = durations.length > 0 ? Math.max(...durations) : 0;
    const heightFor = (durationMs: number | undefined): number => {
      if (!durationMs || maxDuration === 0) return RUN_BAR_MIN_HEIGHT + 4;
      return (
        RUN_BAR_MIN_HEIGHT +
        (durationMs / maxDuration) * (RUN_BAR_MAX_HEIGHT - RUN_BAR_MIN_HEIGHT)
      );
    };

    return html`
      <span class=${styles.runBars}>
        ${runs.map(
          run => html`
            <i
              class=${styles.runBar}
              data-status=${run.status}
              style=${styleMap({ height: `${heightFor(run.durationMs)}px` })}
              title="${new Date(run.startedAt).toLocaleString()}${run.durationMs
                ? ` · ${formatDurationMs(run.durationMs)}`
                : ''}"
            ></i>
          `
        )}
        ${activeRun
          ? html`<i
              class=${styles.runBar}
              data-status="running"
              style=${styleMap({ height: `${RUN_BAR_MAX_HEIGHT}px` })}
            ></i>`
          : nothing}
      </span>
    `;
  }

  // ===========================================================================
  // Formatting helpers
  // ===========================================================================

  private _formatTrigger(
    config: { type?: string; cronPreset?: string; preset?: string } | null
  ): string {
    if (!config)
      return I18n.t('com.streamlain.streamlain.process.schedule.manual');
    const type = config.type ?? 'manual';

    if (type === 'manual')
      return I18n.t('com.streamlain.streamlain.process.schedule.manual');
    if (type === 'webhook')
      return I18n.t('com.streamlain.streamlain.process.schedule.webhook');
    if (type === 'row_created')
      return I18n.t('com.streamlain.streamlain.process.schedule.on-new-row');
    if (type === 'row_changed')
      return I18n.t(
        'com.streamlain.streamlain.process.schedule.on-row-changed'
      );

    if (type === 'scheduled') {
      const preset = config.cronPreset ?? config.preset ?? '';
      const KEY_PREFIX = 'com.streamlain.streamlain.process.schedule.';
      const presetKeyMap: Record<string, string> = {
        every_10_sec: 'every-10-sec',
        every_30_sec: 'every-30-sec',
        every_1_min: 'every-1-min',
        every_5_min: 'every-5-min',
        every_15_min: 'every-15-min',
        every_30_min: 'every-30-min',
        every_hour: 'every-hour',
        hourly: 'every-hour',
        daily_9am: 'daily-9am',
        daily_6pm: 'daily-6pm',
        weekly_monday: 'weekly-monday',
        weekly_monday_9am: 'weekly-monday',
        monthly_first_9am: 'monthly',
        custom: 'custom',
      };
      const key = presetKeyMap[preset];
      return key
        ? I18n.t(`${KEY_PREFIX}${key}`)
        : preset ||
            I18n.t('com.streamlain.streamlain.process.trigger-scheduled');
    }
    return type;
  }

  private _formatLastRun(timestamp: number | null): string {
    if (!timestamp) {
      return I18n.t('com.streamlain.streamlain.process.last-run.never');
    }
    const diff = Date.now() - timestamp;
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) {
      return I18n.t('com.streamlain.streamlain.process.last-run.just-now');
    }
    if (minutes < 60) {
      return I18n.t('com.streamlain.streamlain.process.last-run.minutes-ago', {
        count: minutes,
      });
    }
    const hours = Math.floor(minutes / 60);
    if (hours < 24) {
      return I18n.t('com.streamlain.streamlain.process.last-run.hours-ago', {
        count: hours,
      });
    }
    const days = Math.floor(hours / 24);
    return I18n.t('com.streamlain.streamlain.process.last-run.days-ago', {
      count: days,
    });
  }

  // ===========================================================================
  // Validation & handlers
  // ===========================================================================

  /**
   * sl-optc: Validate automation config before enabling schedule.
   * Prevents infinite loops by requiring:
   * 1. Status tracking enabled (to skip already-processed rows)
   * 2. Match keys when input table == output table (to update, not append)
   * 3. "If not found: Fail" when input == output (no upsert/create allowed)
   *
   * Returns i18n keys for translation in React layer.
   */
  private _validateEnableSchedule(): { valid: boolean; reasonKey?: string } {
    const { sourceTableId, outputTables, triggerConfig } = this.model.props;

    // A row trigger watches its source table
    if (isRowTrigger(triggerConfig?.type) && !sourceTableId) {
      return { valid: false, reasonKey: 'source-table-required' };
    }

    // Rule 1: Automatic runs over a source table mark the rows they take
    // (the contract's rule, as on the server).
    const trackingGap = statusTrackingGap(this.model.props);
    if (trackingGap === 'off') {
      return { valid: false, reasonKey: 'status-tracking-required' };
    }
    if (trackingGap === 'column') {
      return { valid: false, reasonKey: 'status-tracking-incomplete' };
    }
    if (trackingGap === 'values') {
      return { valid: false, reasonKey: 'status-values-missing' };
    }

    // Rule 2 & 3: If input table == output table, special restrictions apply
    if (sourceTableId && outputTables?.length) {
      const inputEqualsOutput = outputTables.some(
        t => t.tableId === sourceTableId
      );

      if (inputEqualsOutput) {
        // sl-mau5: Check if the output table has a row MatchConfig
        const matchTable = outputTables.find(t => t.tableId === sourceTableId);
        const matchConfigs = this.model.props.matchConfigs ?? [];
        const rowMc = matchTable?.matchConfigId
          ? matchConfigs.find(mc => mc.id === matchTable.matchConfigId)
          : undefined;

        if (!rowMc || rowMc.keys.length === 0) {
          return {
            valid: false,
            reasonKey: 'match-key-required',
          };
        }

        // Check notFoundBehavior - must be 'fail', not 'create'
        if (
          rowMc.notFoundBehavior === 'create' ||
          rowMc.notFoundBehavior === undefined
        ) {
          return {
            valid: false,
            reasonKey: 'create-not-allowed',
          };
        }
      }
    }

    return { valid: true };
  }

  private readonly _handleToggleEnabled = (e: Event) => {
    e.stopPropagation();
    const currentConfig = this.model.props.triggerConfig || {};
    const currentEnabled =
      window.__automationRuntimeBridge?.getRuntime(this.model.id)?.schedule
        ?.enabled ??
      currentConfig.enabled ??
      false;
    const newEnabled = !currentEnabled;

    // sl-optc: Validate before enabling
    if (newEnabled) {
      const validation = this._validateEnableSchedule();
      if (!validation.valid) {
        // Dispatch event for React layer to show notification with i18n key
        window.dispatchEvent(
          new CustomEvent('automation-validation-error', {
            detail: {
              blockId: this.model.id,
              reasonKey: validation.reasonKey,
            },
            bubbles: true,
          })
        );
        return; // Don't enable
      }
    }

    const newTriggerConfig = {
      ...currentConfig,
      type: currentConfig.type || 'scheduled',
      cronPreset: currentConfig.cronPreset || 'every_5_min',
      enabled: newEnabled,
    };

    this.model.props.triggerConfig = newTriggerConfig;

    window.dispatchEvent(
      new CustomEvent('automation-trigger-sync', {
        detail: {
          blockId: this.model.id,
          docId: this.model.store.id,
          triggerConfig: newTriggerConfig,
        },
        bubbles: true,
      })
    );
  };

  /**
   * Run now (manual trigger) or again after a failed run. Dispatches the same
   * `automation-run` event the global execution listener
   * (use-execution-listener.ts) handles for the sidebar Run button.
   */
  private readonly _handleRun = (e: Event) => {
    e.stopPropagation();
    // A manual run over a table runs the rows chosen in it: the sidebar's
    // Run button counts them and explains how to choose them.
    if (this.model.props.sourceTableId) {
      this._openSettings()(e);
      return;
    }
    const docId = this.model.store.id;
    if (!docId) return;

    window.dispatchEvent(
      new CustomEvent('automation-run', {
        detail: {
          blockId: this.model.id,
          docId,
          mode: 'execute',
        },
        bubbles: true,
      })
    );
  };

  private readonly _stopPointer = (event: Event) => {
    event.stopPropagation();
  };

  /**
   * A jump into the sidebar: the start page, or the settings at `tile`.
   * `attention` points the setting out, as an open item of the sidebar does.
   */
  private _openSettings(tile?: ReadinessTileId, attention = false) {
    return (event: Event) => {
      event.stopPropagation();

      window.dispatchEvent(
        new CustomEvent('automation-configure', {
          detail: { blockId: this.model.id, tile, attention },
          bubbles: true,
        })
      );

      this.std
        .getOptional(SidebarExtensionIdentifier)
        ?.open('streamlain-automation');
    };
  }
}

// =============================================================================
// Interaction Extension
// =============================================================================

export const AutomationBlockInteraction =
  GfxViewInteractionExtension<AutomationCanvasBlockComponent>(
    AutomationBlockSchema.model.flavour,
    {
      selectionBorderRadius: 8,
      resizeConstraint: {
        allowedHandlers: [],
      },

      handleRotate: () => ({
        beforeRotate(context) {
          context.set({ rotatable: false });
        },
      }),

      handleSelection: ({ gfx, view, model }) => ({
        onSelect(context) {
          const { selected, multiSelect } = context;
          const { editing } = gfx.selection;
          const alreadySelected = gfx.selection.has(model.id);

          if (!multiSelect && selected && (alreadySelected || editing)) {
            if (model.isLocked()) return;

            if (alreadySelected && editing) {
              return;
            }

            gfx.selection.set({
              elements: [model.id],
              editing: true,
            });

            view.updateComplete
              .then(() => {
                if (!view.isConnected) return;
              })
              .catch(console.error);
          } else {
            context.default(context);
          }
        },
      }),
    }
  );
